import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import StyleIcon from '@mui/icons-material/Style'; // Icon for strategies
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const FramingDiscourseAnalysisTranscript = ({ summaryData }) => {
  const [expandedFrame, setExpandedFrame] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFrame(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Framing Discourse analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box
        px={2}
        py={1.5}
        // Use a neutral or theme-based color for the header
        bgcolor="grey.100" // Example: light grey background
        borderBottom={1}
        borderColor="divider"
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Framing & Discourse Analysis
          <AnalysisLensHelperText lensName="framing_discourse" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary & Key Metrics */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {results.summary || 'No summary provided.'}
          </Typography>
          <Grid container spacing={1} alignItems="baseline">
            <Grid item>
              <Typography variant="subtitle2">Dominant Frame:</Typography>
            </Grid>
            <Grid item>
              <Chip
                icon={<LabelImportantIcon fontSize="small" />}
                label={results.dominant_frame || 'N/A'}
                size="small"
                color="primary"
                variant="filled"
                sx={{ fontWeight: 'medium' }}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2">Total Frames Identified:</Typography>
            </Grid>
            <Grid item>
              <Chip label={results.frame_count ?? 'N/A'} size="small" variant="outlined" />
            </Grid>
          </Grid>
        </Box>

        {/* Frames Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Identified Frames
        </Typography>

        {results.frames?.length > 0 ? (
          results.frames.map((frame, index) => {
            const panelId = `frame-panel-${index}`;
            const isDominant = frame.label === results.dominant_frame;
            const isExpanded = expandedFrame === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': {
                    display: 'none', // Remove default top border
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 12px 0',
                    borderBottom: `1px solid ${isDominant ? 'primary.main' : 'divider'}`, // Highlight dominant frame border when expanded
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded
                      ? isDominant
                        ? 'primary.lighter'
                        : 'grey.50'
                      : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {isDominant && (
                      <Tooltip title="Dominant Frame">
                        <LabelImportantIcon color="primary" fontSize="small" />
                      </Tooltip>
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                      {frame.label}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    {frame.description}
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Discursive Strategies */}
                    {frame.discursive_strategies?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <StyleIcon fontSize="inherit" color="action" /> Discursive Strategies
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {frame.discursive_strategies.map((strategy, idx) => (
                            <Chip
                              key={idx}
                              label={strategy}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Participants Using */}
                    {frame.participants_using?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <RecordVoiceOverIcon fontSize="inherit" color="action" /> Participants
                          Using
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {frame.participants_using.map((participant, idx) => (
                            <Chip key={idx} label={participant} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Quotes */}
                    {frame.quotes?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FormatQuoteIcon fontSize="inherit" color="action" /> Supporting Quotes
                        </Typography>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {frame.quotes.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 1 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific frames identified in this analysis.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default FramingDiscourseAnalysisTranscript;
